let makerjs = require('makerjs')

const DEFAULT_OPTIONS = {
  bezierAccuracy: 0.25,
  combine: false,
  centerCharacterOrigin: false,
  opentypeOptions: { kerning: false }
}

/**
 * Create text model optimized for CNC engraving with single-stroke fonts
 * Handles deduplication of overlapping paths common in stick fonts
 * @param {opentype.Font} font - The loaded opentype font
 * @param {string} text - Text to render
 * @param {number} fontSize - Font size in mm
 * @param {Object} options - Override default options
 * @returns {Object} Makerjs model with deduplicated paths
 */
function createEngravingText(font, text, fontSize, options = {}) {
  const opts = { ...DEFAULT_OPTIONS, ...options }

  const textModel = new makerjs.models.Text(
    font,
    text,
    fontSize,
    opts.combine,
    opts.centerCharacterOrigin,
    opts.bezierAccuracy,
    opts.opentypeOptions
  )

  return deduplicatePaths(textModel, 0.5)
}

/**
 * Remove duplicate/overlapping paths from a model
 * CamBam stick fonts trace back over the same path, creating duplicates
 * @param {Object} model - Makerjs model to deduplicate
 * @param {number} tolerance - Path matching tolerance in mm
 * @returns {Object} Model with duplicates removed
 */
function deduplicatePaths(model, tolerance) {
  const pathMap = new Map()
  const pathsToRemove = []

  makerjs.model.walk(model, {
    onPath: function (walkPath) {
      const key = computePathKey(walkPath.pathContext, walkPath.offset, tolerance)

      if (pathMap.has(key)) {
        pathsToRemove.push({
          modelContext: walkPath.modelContext,
          pathId: walkPath.pathId
        })
      } else {
        pathMap.set(key, walkPath)
      }
    }
  })

  for (const toRemove of pathsToRemove) {
    if (toRemove.modelContext.paths && toRemove.modelContext.paths[toRemove.pathId]) {
      delete toRemove.modelContext.paths[toRemove.pathId]
    }
  }

  return model
}

/**
 * Compute a canonical key for a path that is direction-independent
 * A->B produces the same key as B->A
 */
function computePathKey(path, offset, tolerance) {
  const endpoints = getPathEndpoints(path, offset)
  const start = roundToGrid(endpoints.start, tolerance)
  const end = roundToGrid(endpoints.end, tolerance)

  // Sort points to make key direction-independent
  const sortedPoints = [start, end].sort((a, b) => a[0] - b[0] || a[1] - b[1])

  return `${path.type}:${sortedPoints[0].join(',')}:${sortedPoints[1].join(',')}`
}

/**
 * Get the start and end points of any path type
 */
function getPathEndpoints(path, offset) {
  const ox = offset ? offset[0] : 0
  const oy = offset ? offset[1] : 0

  switch (path.type) {
    case 'line':
      return {
        start: [path.origin[0] + ox, path.origin[1] + oy],
        end: [path.end[0] + ox, path.end[1] + oy]
      }

    case 'arc': {
      const startAngleRad = (path.startAngle * Math.PI) / 180
      const endAngleRad = (path.endAngle * Math.PI) / 180
      return {
        start: [
          path.origin[0] + path.radius * Math.cos(startAngleRad) + ox,
          path.origin[1] + path.radius * Math.sin(startAngleRad) + oy
        ],
        end: [
          path.origin[0] + path.radius * Math.cos(endAngleRad) + ox,
          path.origin[1] + path.radius * Math.sin(endAngleRad) + oy
        ]
      }
    }

    case 'circle':
      return {
        start: [path.origin[0] + ox, path.origin[1] + oy],
        end: [path.origin[0] + ox, path.origin[1] + oy]
      }

    default:
      if (path.seed && path.seed.length >= 2) {
        const first = path.seed[0]
        const last = path.seed[path.seed.length - 1]
        return {
          start: [first[0] + ox, first[1] + oy],
          end: [last[0] + ox, last[1] + oy]
        }
      }
      return { start: [ox, oy], end: [ox, oy] }
  }
}

function roundToGrid(point, tolerance) {
  return [Math.round(point[0] / tolerance) * tolerance, Math.round(point[1] / tolerance) * tolerance]
}

export default {
  createEngravingText,
  deduplicatePaths,
  DEFAULT_OPTIONS
}
